import { apiService } from './api';
import { Order } from '../types';

export const orderService = {
  async getOpenOrders(org: string, restaurantId?: string | null): Promise<Order[]> {
    const response = await apiService.get<Order[]>('/orders/open', {
      org,
      ...(restaurantId && { restaurantId })
    });
    return response.data;
  },

  async getOrder(id: string): Promise<Order> {
    const response = await apiService.get<Order>(`/orders/${id}`);
    return response.data;
  },

  async openOrder(org: string, tableId: string, notes?: string): Promise<Order> {
    const params = new URLSearchParams({ org, tableId });
    if (notes) params.append('notes', notes);
    const response = await apiService.post<Order>(`/orders/open?${params.toString()}`, null);
    return response.data;
  },

  async addOrderItem(orderId: string, itemId: string, qty: number, notes?: string): Promise<Order> {
    const params = new URLSearchParams({ itemId, qty: qty.toString() });
    if (notes) params.append('notes', notes);
    const response = await apiService.post<Order>(`/orders/${orderId}/items?${params.toString()}`, null);
    return response.data;
  },

  async removeOrderItem(orderId: string, itemId: string): Promise<Order> {
    const response = await apiService.delete<Order>(`/orders/${orderId}/items/${itemId}`);
    return response.data;
  },

  async applyDiscount(orderId: string, percent?: number, flat?: number): Promise<Order> {
    const params = new URLSearchParams();
    if (percent !== undefined) params.append('percent', percent.toString());
    if (flat !== undefined) params.append('flat', flat.toString());
    const response = await apiService.patch<Order>(`/orders/${orderId}/discount?${params.toString()}`, null);
    return response.data;
  },

  async settleOrder(settlementData: {
    orderId: string;
    paymentMode: string;
    customerId?: string;
    customerName?: string;
    customerMobile?: string;
    customerEmail?: string;
    customerAddress?: string;
    discountPercent?: number;
    discountAmount?: number;
  }): Promise<Order> {
    const { orderId, ...requestData } = settlementData;
    const response = await apiService.patch<Order>(`/orders/${orderId}/settle`, requestData);
    return response.data;
  },

  async addDuePayment(orderId: string, amount: number): Promise<Order> {
    const params = new URLSearchParams({ amount: amount.toString() });
    const response = await apiService.patch<Order>(`/orders/${orderId}/due-payment?${params.toString()}`, null);
    return response.data;
  },

  async fetchOrderDetailsByTable(org: string, tableId: string): Promise<Order> {
    const params = new URLSearchParams({ org, tableId });
    const response = await apiService.post<Order>(`/orders/open?${params.toString()}`, null);
    return response.data;
  },

  async placeOrder(orderData: {
    organizationCode: string;
    tableId: string;
    paymentMode: string;
    notes?: string;
    customerName?: string;
    customerMobile?: string;
    customerEmail?: string;
    customerAddress?: string;
    discountPercent?: number;
    discountAmount?: number;
    items: Array<{
      itemId: string;
      quantity: number;
      specialNotes?: string;
    }>;
  }): Promise<any> {
    const response = await apiService.post('/orders/place', orderData);
    return response.data;
  },

  async getOrdersByCustomer(org: string, customerId: string, page: number = 0, size: number = 20): Promise<any> {
    const params = new URLSearchParams({ org, customerId, page: page.toString(), size: size.toString() });
    const response = await apiService.get(`/orders/by-customer?${params.toString()}`);
    return response.data;
  },

  async getOrdersByDateRange(org: string, start: string, end: string, page: number = 0, size: number = 20): Promise<any> {
    const params = new URLSearchParams({ org, start, end, page: page.toString(), size: size.toString() });
    const response = await apiService.get(`/orders/by-date?${params.toString()}`);
    return response.data;
  },

  async updateOrder(orderId: string, updateData: {
    notes?: string;
    discountPercent?: number;
    discountAmount?: number;
    items?: Array<{
      itemId: string;
      quantity: number;
      specialNotes?: string;
    }>;
  }): Promise<Order> {
    const response = await apiService.patch<Order>(`/orders/${orderId}`, updateData);
    return response.data;
  }
};